import { useScrollReveal } from '../../hooks/useScrollReveal';

const allAreas = [
  'Charleston', 'Mount Pleasant', 'North Charleston', 'Summerville',
  'Isle of Palms', 'Kiawah Island', 'Seabrook Island', 'Walterboro',
  'Daniel Island', 'James Island', 'Folly Beach', 'Goose Creek',
  'Hanahan', "Sullivan's Island", 'Moncks Corner', 'Ladson',
];

interface PageAreasProps {
  headline?: string;
  accentLine?: string;
  description?: string;
  highlightCity?: string;
}

export default function PageAreas({
  headline = 'Serving Charleston',
  accentLine = '& Surrounding Areas',
  description = 'Strategically positioned for fast airport pickups and citywide service across Charleston County, Berkeley County, Dorchester County, and beyond.',
  highlightCity,
}: PageAreasProps) {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-28" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="section-reveal grid lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <span className="text-gold text-xs tracking-widest uppercase font-semibold">Where We Operate</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3 mb-4 leading-tight">
              {headline}<br />
              <span className="text-gold">{accentLine}</span>
            </h2>
            <p className="text-white/45 text-sm leading-relaxed mb-10">{description}</p>

            <div className="grid grid-cols-2 gap-1">
              {allAreas.map((area) => (
                <div
                  key={area}
                  className={`flex items-center gap-2.5 py-2.5 px-3 border-b border-white/5 hover:border-gold/20 transition-colors group ${
                    highlightCity && area.toLowerCase().includes(highlightCity.toLowerCase())
                      ? 'bg-gold/10 border-gold/20'
                      : ''
                  }`}
                >
                  <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-map-pin-2-fill text-xs ${highlightCity && area.toLowerCase().includes(highlightCity.toLowerCase()) ? 'text-gold' : 'text-gold/50 group-hover:text-gold'} transition-colors`}></i>
                  </div>
                  <span className={`text-xs tracking-wide transition-colors ${highlightCity && area.toLowerCase().includes(highlightCity.toLowerCase()) ? 'text-white font-semibold' : 'text-white/55 group-hover:text-white'}`}>{area}</span>
                </div>
              ))}
            </div>

            <p className="mt-6 text-white/25 text-xs leading-relaxed">
              Also serving: Awendaw · Bonneau · Cottageville · Dorchester · Edisto Beach · Grover · Harleyville · Hollywood · Jamestown · Knightsville · McClellanville · Meggett · Ravenel · Ridgeville · Rockville · Smoaks · St. George · St. Stephen · Williams and more.
            </p>
          </div>

          {/* Map */}
          <div>
            <div className="w-full h-[440px] border border-gold/20 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d212990.9478706748!2d-80.27272419082031!3d32.81071959999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88fe7a42408e5b27%3A0x7f8d92e0ffa96dae!2sCharleston%2C%20SC!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(40%) contrast(1.1) brightness(0.9)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="TECS Limo Service - Charleston SC Coverage Area"
              ></iframe>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="bg-neutral-900 border border-gold/15 p-4 text-center">
                <div className="text-gold font-display text-xl font-bold">20+</div>
                <div className="text-white/40 text-xs mt-1">Cities Served</div>
              </div>
              <div className="bg-neutral-900 border border-gold/15 p-4 text-center">
                <div className="text-gold font-display text-xl font-bold">3</div>
                <div className="text-white/40 text-xs mt-1">Airports Covered</div>
              </div>
              <div className="bg-neutral-900 border border-gold/15 p-4 text-center">
                <div className="text-gold font-display text-xl font-bold">24/7</div>
                <div className="text-white/40 text-xs mt-1">Service Hours</div>
              </div>
            </div>
            <div className="mt-4">
              <a
                href="https://maps.app.goo.gl/mtvQL3szeLejFWnH9"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 text-gold text-xs tracking-wide hover:text-gold-light transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-2-line text-sm"></i></div>
                View Full Service Area on Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
